import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Stack } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/lead-staging", label: "Lead Staging", icon: <Stack size={18} weight="duotone" />, testId: "nav-admin-lead-staging" }];

export default function AdminLeadStagingPage() {
  const confirmEdit = useEditConfirm();
  const [batchId, setBatchId] = useState("");
  const [repCount, setRepCount] = useState("15");
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState(null);
  const [reps, setReps] = useState([]);
  const [assignRepId, setAssignRepId] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setReps(data.filter((u) => u.role === "affiliate"))).catch(() => {});
  }, []);

  const targetTrust = (parseInt(repCount, 10) || 0) * 36;
  const targetFunding = (parseInt(repCount, 10) || 0) * 50;

  const buildPool = async () => {
    if (!batchId.trim()) { toast.error("Enter a batch ID (e.g. sept15_cohort)"); return; }
    setBuilding(true);
    try {
      const { data } = await api.post("/admin/leads/stage-pool", {
        batch_id: batchId.trim(),
        trust_target: targetTrust,
        funding_target: targetFunding,
      });
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBuilding(false);
    }
  };

  const checkStatus = async () => {
    if (!batchId.trim()) { toast.error("Enter a batch ID to check"); return; }
    try {
      const { data } = await api.get("/admin/leads/staging-pool-status", { params: { batch_id: batchId.trim() } });
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignToRep = async () => {
    if (!assignRepId) { toast.error("Pick a rep to assign to"); return; }
    if (!batchId.trim()) { toast.error("Enter a batch ID"); return; }
    const rep = reps.find((r) => r.id === assignRepId);
    if (!(await confirmEdit(`${rep?.display_name || "this rep"}'s staged lead assignment`))) return;
    setAssigning(true);
    try {
      const { data } = await api.post(`/admin/reps/${assignRepId}/assign-staged-leads`, { batch_id: batchId.trim() });
      toast.success(data.message);
      checkStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Lead Staging" moduleBackPath="/admin">
      <div data-testid="admin-lead-staging-page" className="max-w-2xl space-y-8">
        <div>
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Pre-load and verify a batch of leads before you know which rep they'll go to.">Lead Staging Pool</h1>
          <p className="text-igc-muted text-sm">Pre-pull, clean and verify a batch of leads before you have rep names — then assign instantly the moment a name lands.</p>
        </div>

        <div className="bg-igc-surface border border-igc-gold/20 p-6 space-y-4">
          <h2 className="text-igc-cream font-serif text-lg">1. Build a staging pool</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-igc-muted text-xs block mb-1">Batch ID</label>
              <Input data-testid="staging-batch-id-input" value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="sept15_cohort" title="A short name for this batch of staged leads — you'll use this same ID to check status and assign later." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
            </div>
            <div>
              <label className="text-igc-muted text-xs block mb-1">Number of reps</label>
              <Input data-testid="staging-rep-count-input" type="number" value={repCount} onChange={(e) => setRepCount(e.target.value)} title="How many new reps you're planning to onboard from this batch — sets how many leads get staged." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
            </div>
          </div>
          <p className="text-igc-muted text-xs">Will stage {targetTrust} trust + {targetFunding} funding leads (36 + 50 per rep, with built-in buffer to absorb re-verification at assignment).</p>
          <div className="flex gap-3">
            <Button data-testid="build-staging-pool-button" onClick={buildPool} disabled={building} title="Starts pulling and verifying leads into this staging pool. Can take several minutes." className="bg-igc-gold hover:bg-igc-goldHover text-black">
              {building ? "Starting..." : "Build Staging Pool"}
            </Button>
            <Button data-testid="check-staging-status-button" onClick={checkStatus} variant="outline" title="Checks how many trust/funding leads are currently staged for this batch ID." className="border-igc-gold/30 text-igc-cream">
              Check Status
            </Button>
          </div>
          {status && (
            <div data-testid="staging-pool-status-display" className="bg-igc-surfaceLight border border-igc-gold/20 p-4 text-sm text-igc-cream space-y-1">
              <p>Trust staged: <span className="text-igc-goldText">{status.trust}</span> / {targetTrust || "—"}</p>
              <p>Funding staged: <span className="text-igc-goldText">{status.funding}</span> / {targetFunding || "—"}</p>
            </div>
          )}
        </div>

        <div className="bg-igc-surface border border-igc-gold/20 p-6 space-y-4">
          <h2 className="text-igc-cream font-serif text-lg">2. Assign staged leads to a named rep</h2>
          <p className="text-igc-muted text-xs">Instant — no new DataMoon pull. Re-scrubs DNC only (fail-closed) right before handoff.</p>
          <Select value={assignRepId} onValueChange={setAssignRepId}>
            <SelectTrigger data-testid="staging-assign-rep-select" title="Choose which rep receives this batch's staged leads." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              <SelectValue placeholder="Select a rep" />
            </SelectTrigger>
            <SelectContent>
              {reps.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`staging-assign-rep-option-${r.user_id}`}>{r.user_id} — {r.display_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="assign-staged-leads-button" onClick={assignToRep} disabled={assigning} title="Instantly hands off the staged leads for this batch to the selected rep." className="bg-igc-gold hover:bg-igc-goldHover text-black w-full">
            {assigning ? "Assigning..." : "Assign 36 Trust + 50 Funding From Staging Pool"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
